import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';

const resolveSubscriptionLabel = (subscription) => {
  if (!subscription) {
    return 'Unknown';
  }

  return (
    subscription.customName ||
    subscription.displayName ||
    subscription.name ||
    subscription.subscriptionTypeId ||
    'Unknown'
  );
};

const BulkReassignSensorsModal = ({
  open,
  onClose,
  organizationId,
  organizationName,
  organizationSubscriptions = [],
  sensorAssignments = [],
  onConfirm
}) => {
  const [selectedSubscriptionTypeId, setSelectedSubscriptionTypeId] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedSubscriptionTypeId('');
    }
  }, [open]);

  const availableSubscriptions = useMemo(
    () =>
      organizationSubscriptions.filter(
        (subscription) =>
          subscription.status !== 'inactive' &&
          subscription.subscriptionTypeId
      ),
    [organizationSubscriptions]
  );

  const disabled = !organizationId || availableSubscriptions.length <= 1 || sensorAssignments.length === 0;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedSubscriptionTypeId || disabled) {
      return;
    }
    onConfirm?.(selectedSubscriptionTypeId);
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <WalteroTypography variant="h5" sx={{ color: 'waltero.primary.coreBlue', fontWeight: 600 }}>
            Reassign All Sensors
          </WalteroTypography>
          <WalteroTypography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Organization: {organizationName || organizationId || 'Unknown'}
          </WalteroTypography>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            This will reassign <strong>{sensorAssignments.length}</strong> sensors to a new subscription.
            Sensors already using the selected subscription will be skipped.
          </Alert>

          {availableSubscriptions.length <= 1 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              At least two active subscriptions are required to perform a bulk reassignment.
            </Alert>
          )}

          {sensorAssignments.length === 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              No sensors found for this organization.
            </Alert>
          )}

          <FormControl fullWidth sx={{ minWidth: 280 }} disabled={disabled}>
            <InputLabel id="bulk-reassign-subscription-select">Target Subscription *</InputLabel>
            <Select
              labelId="bulk-reassign-subscription-select"
              value={selectedSubscriptionTypeId}
              label="Target Subscription *"
              onChange={(event) => setSelectedSubscriptionTypeId(event.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    minWidth: 320
                  }
                }
              }}
            >
              {availableSubscriptions.map((subscription) => (
                <MenuItem key={subscription.id} value={subscription.subscriptionTypeId}>
                  {resolveSubscriptionLabel(subscription)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <WalteroButton onClick={onClose} variant="outlined">
            CANCEL
          </WalteroButton>
          <WalteroButton
            type="submit"
            variant="contained"
            disabled={disabled || !selectedSubscriptionTypeId}
          >
            REASSIGN
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BulkReassignSensorsModal;
